import React, { useState, useEffect, useContext } from "react";
import { Switch, Route } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import Loading from "../components/common/loading";
// import Select from "./common/select";
import ListGroup from "../components/common/listGroup";
import http from "../services/http";
import UserContext from "../context/userContext";
import SidebarContext from "../context/sidebarContext";

import Home from "../components/dashboards/home";
import Custom from "../components/dashboards/custom";
import OpSum from "../components/dashboards/opsum";

import SelectDate from "../components/common/selectDate";

const Dashboards = ({ location }) => {
  const [user] = useContext(UserContext);
  const [sidebar] = useContext(SidebarContext);

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(moment());
  // const [focused, setFocused] = useState(null);
  const [corpDashboards, setCorpDashboards] = useState([]);
  const [customDashboards, setCustomDashboards] = useState([]);
  const [dashboard, setDashboard] = useState("");
  const [dashboardType, setDashboardType] = useState("");

  const [site, setSite] = useState({ id: "" });
  const [type, setType] = useState({ id: "" });

  const types = [
    { id: "BC", name: "Campaign" },
    { id: "JR", name: "Juice Run" },
  ];

  const sites = [
    { id: "BA", name: "Bardney" },
    { id: "BU", name: "Bury" },
    { id: "CA", name: "Cantley" },
    { id: "NE", name: "Newark" },
    { id: "WI", name: "Wissington" },
    { id: "CO", name: "Central Office" },
  ];

  useEffect(() => {
    document.title = "FTR - Dashboards";
    return () => {
      document.title = "FTR";
    };
  }, []);

  useEffect(() => {
    async function getOperations() {
      const sessionType = sessionStorage.getItem("ftr-dashboard-type");

      try {
        const { data: operations } = await http.get(`/operations/active`);
        if (sessionType) {
          setType(JSON.parse(sessionType));
        } else if (operations.length > 0) {
          setType({ id: operations[0].TYPE_ID });
        }
      }
      catch {
        //ignore error
      }
    }

    async function getCorpDashboards() {
      try {
        const { data } = await http.get(`/dashboards?manageId=1&formatId=1`);
        setCorpDashboards(data);
      }
      catch {
        //ignore error
      }
    }

    async function getCustomDashboards() {
      try {
        const { data } = await http.get(`/dashboards/${user.SITE}?manageId=2&formatId=1`);
        setCustomDashboards(data);
      }
      catch {
        //ignore error
      }
    }

    function getSite() {
      const sessionSite = sessionStorage.getItem("ftr-dashboard-site");
      if (sessionSite) {
        setSite(JSON.parse(sessionSite));
      } else {
        setSite({ id: user.SITE });
      }
    }
    if (user) {
      getOperations();
      getCorpDashboards();
      getCustomDashboards();
      getSite();
    }
  }, [user]);

  useEffect(() => {
    async function getData() {
      setLoading(true);
      const apiDate = moment(date).format("YYYY-MM-DD");
      try {
        const { data } = await http.get(`/dashboard-data/${site.id}/${dashboard}/${type.id}/${apiDate}`);
        setData(data);
      } catch (ex) {
        setData({});
        toast.error("There was a problem running this dashboard");
      }
      setLoading(false);
    }

    if (!dashboard) {
      setData({});
    }
    
    if (user && type.id && site.id && dashboard) {
      getData();
    }
  }, [user, type, site, date, dashboard]);

  useEffect(() => {
    const allDashboards = corpDashboards.concat(customDashboards);
    if (allDashboards.length === 0) return;
    setData({});
    const dashboardLocation = location.pathname.split("/")[2];
    const dashboard = allDashboards.find(
      (d) => dashboardLocation === d.ADDRESS
    );
    if (!dashboard) setDashboard("");
    if (!dashboard) setDashboardType("");
    if (dashboard) setDashboard(dashboard.ID);
    if (dashboard) setDashboardType(dashboard.DATA_TYPE);
  }, [location, corpDashboards, customDashboards]);

  async function handleRefresh() {
    if (!dashboard) return;
    setLoading(true);
    const apiDate = moment(date).format("YYYY-MM-DD");
    try {
      const { data } = await http.get(
        `/dashboard-data/${site.id}/${dashboard}/${type.id}/${apiDate}`
      );
      setData(data);
    } catch (ex) {
      setData({});
      toast.error("There was a problem running this dashboard");
    }
    setLoading(false);
  }

  const handleSiteSelect = (site) => {
    sessionStorage.setItem("ftr-dashboard-site", JSON.stringify(site));
    setSite(site);
  };

  const handleTypeChange = (type) => {
    sessionStorage.setItem("ftr-dashboard-type", JSON.stringify(type));
    setType(type);
  };

  let filteredSites = [...sites];
  if (dashboardType === "C") {
    filteredSites = filteredSites.filter((s) => false);
  }
  if (dashboardType === "S") {
    filteredSites = filteredSites.filter((s) => s.id !== "CO");
  }

  function handleDateChange({ currentTarget: input }) {
    setDate(moment(input.value));
  }

  return (
    <div className={sidebar ? "col-10 mt-4" : "col-12 mt-4"}>
      {/* Dashboard Controls */}
      <div className="row">
        <div className="col-auto">
          <ListGroup
            items={types}
            selectedItem={type}
            onItemSelect={handleTypeChange}
          />
        </div>
        {user.SITE === "CO" && (
          <div className="col-auto">
            <ListGroup
              items={filteredSites}
              selectedItem={site}
              onItemSelect={handleSiteSelect}
            />
          </div>
        )}
        <div className="col-auto">
          <SelectDate
            value={date.format("YYYY-MM-DD")}
            onChange={handleDateChange}
          />
        </div>
        <div className="col">
          <button className="btn btn-primary" onClick={handleRefresh}>
            <i className="fas fa-sync-alt mr-1"></i>
            Refresh
          </button>
        </div>
      </div>
      {/* Dashboard */}
      {loading ? (
        <Loading />
      ) : (
        <Switch>
          <Route
            path="/dashboards/"
            exact
            render={(props) => (
              <Home
                user={user}
                dashboards={corpDashboards}
                custom={customDashboards}
                site={site}
                {...props}
              />
            )}
          />
          <Route
            path="/dashboards/opsum"
            render={(props) => (
              <OpSum
                user={user}
                data={data}
                {...props} />
            )}
          />
          {/* Custom Dashboards */}
          <Route
            path="/dashboards/:address"
            render={(props) => (
              <Custom
                user={user}
                data={data}
                {...props} />
            )}
          />
        </Switch>
      )}
    </div>
  );
};

export default Dashboards;
